// Candidate pages in the interview's language (docs/PORTAL_I18N.md, area 11).
//
// Its own i18next instance, separate from the recruiter portal's: a recruiter
// who opens an interview in their own browser must not carry their portal
// language onto what candidates see, and candidate pages never write the
// portal's stored choice. Same scheme otherwise: the English text is the key,
// catalogs are files (locales/candidate/<code>.json, one per interview
// language), a missing key shows the English, nothing calls a language model.
//
// Where the language comes from: the interview room's languageCode, the role's
// interviewLanguageCode on the career pages, the browser on /my-interviews.

import i18next, { type TFunction } from "i18next";
import { useEffect, useState, type ReactNode } from "react";
import { I18nextProvider } from "react-i18next";

import interviewLanguages from "../interviewLanguages.json";
import { languagesEnabled, pseudoText } from "./index";
import { isComplexScript, loadScriptFont } from "./scriptFonts";

const catalogs = import.meta.glob<Record<string, string>>("../../locales/candidate/*.json", { import: "default" });

// Layout tests on staging and local: ?ui=en-XA (long, accented) or ?ui=ar-XB
// (the same, right to left), or ?ui=<catalog code> to see any language.
const PSEUDO_LONG = "en-XA";
const PSEUDO_RTL = "ar-XB";

/** Catalog code for an interview code: "de-AT" -> "de"; Chinese keeps its
 * script (zh-CN -> zh-Hans, zh-TW and zh-HK -> zh-Hant). Same rule as
 * backend myapp/i18n.py candidate_language. */
export function candidateCatalogCode(interviewCode?: string | null): string {
  const code = (interviewCode || "en").trim();
  if (code.startsWith("zh")) return code === "zh-TW" || code === "zh-HK" ? "zh-Hant" : "zh-Hans";
  return code.split("-")[0].toLowerCase() || "en";
}

const RTL = new Set(
  (interviewLanguages as { code: string; rtl?: boolean }[]).filter((l) => l.rtl).map((l) => candidateCatalogCode(l.code)),
);

export function isRtl(code: string): boolean {
  return code === PSEUDO_RTL || RTL.has(code);
}

function hasCatalog(code: string): boolean {
  return Boolean(catalogs[`../../locales/candidate/${code}.json`]);
}

/** The browser's first language that has a candidate catalog, else "en". */
export function browserCandidateLanguage(allowed?: string[]): string {
  const wanted = typeof navigator === "undefined" ? [] : navigator.languages || [navigator.language];
  for (const tag of wanted) {
    const code = candidateCatalogCode(tag);
    if (code === "en") return "en";
    if ((!allowed || allowed.includes(code)) && hasCatalog(code)) return code;
  }
  return "en";
}

export const candidateI18n = i18next.createInstance();
candidateI18n
  .use({
    type: "postProcessor",
    name: "pseudo",
    process: (value: string) =>
      candidateI18n.language === PSEUDO_LONG || candidateI18n.language === PSEUDO_RTL ? pseudoText(value) : value,
  })
  .init({
    lng: "en",
    fallbackLng: false,
    resources: {},
    keySeparator: false,
    nsSeparator: false,
    returnEmptyString: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    postProcess: ["pseudo"],
  });

function overrideFromUrl(): string | null {
  const host = window.location.hostname;
  if (!(host === "localhost" || host === "127.0.0.1" || host.startsWith("staging."))) return null;
  return new URLSearchParams(window.location.search).get("ui");
}

async function loadCatalog(code: string): Promise<string> {
  if (code !== "en" && code !== PSEUDO_LONG && code !== PSEUDO_RTL) {
    if (!hasCatalog(code)) code = "en";
    else if (!candidateI18n.hasResourceBundle(code, "translation")) {
      const data = await catalogs[`../../locales/candidate/${code}.json`]();
      candidateI18n.addResourceBundle(code, "translation", data, true, true);
    }
  }
  return code;
}

async function activate(code: string): Promise<string> {
  code = await loadCatalog(code);
  await Promise.all([candidateI18n.changeLanguage(code), loadScriptFont(code)]);
  return code;
}

/** A `t` for one candidate language that switches nothing on the page: text a
 * recruiter hands to candidates from the portal (a hiring link's share text). */
export async function candidateFixedT(code: string): Promise<TFunction> {
  return candidateI18n.getFixedT(await loadCatalog(code));
}

/**
 * Puts a candidate page in `code` (a catalog code from candidateCatalogCode or
 * browserCandidateLanguage) while it is open: strings, <html lang dir>, the
 * script's font. On everywhere, like the portal (languagesEnabled).
 * Returns true once the language is in place, so a page can wait before its
 * first paint instead of flashing English.
 */
export function useCandidateLanguage(code: string | null | undefined): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (code == null) return;
    let cancelled = false;
    const wanted = overrideFromUrl() || (languagesEnabled() ? code : "en");
    void activate(wanted).then((active) => {
      if (cancelled) return;
      const root = document.documentElement;
      root.lang = active === PSEUDO_LONG || active === PSEUDO_RTL ? "en" : active;
      root.dir = isRtl(active) ? "rtl" : "ltr";
      if (isComplexScript(active)) root.dataset.script = "complex";
      else delete root.dataset.script;
      setReady(true);
    });
    return () => {
      cancelled = true;
      const root = document.documentElement;
      root.lang = "en";
      root.dir = "ltr";
      delete root.dataset.script;
      root.style.removeProperty("--font-script");
    };
  }, [code]);
  return ready;
}

/** Wraps a candidate page so useTranslation() inside it reads the candidate catalogs. */
export function CandidateI18n({ children }: { children: ReactNode }) {
  return <I18nextProvider i18n={candidateI18n}>{children}</I18nextProvider>;
}

/** Outside components (toast helpers, api error fallbacks). */
export const tc = candidateI18n.t.bind(candidateI18n);
